import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import { useDeviceInfo, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import LicenseRequired from '~Scenes/Settings-v2/Components/LicenseRequired';
import { gql, useQuery } from '@apollo/client';
import { GetTicketTemplate, GetTicketTemplateVariables } from './__queries__';
import { HIDE_IN, TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';

const TICKET_TEMPLATE_QUERY = gql`
  query GetTicketTemplate($venueId: String!) {
    operativeViewer {
      ticketTemplates(venueId: $venueId) {
        id
        stationIds
      }
    }
  }
`;

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const KitchenTicketsSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId);
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');
  const licenses = useLicenses();
  const deviceInfo = useDeviceInfo();

  const ticketTemplateQuery = useQuery<GetTicketTemplate, GetTicketTemplateVariables>(
    TICKET_TEMPLATE_QUERY,
    {
      variables: { venueId: venueId! },
      skip: !venueId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const templates = ticketTemplateQuery.data?.operativeViewer?.ticketTemplates || [];
  // Check if there is a template that is for all stations (aka default template). If there is a template, we hide settings that are overridden by the templates to avoid confusion.
  const hasDefaultTemplate = templates.some((template) => template.stationIds.length === 0);

  // On handheld, this page needs an ordering license; tablets always have it
  const hasLicense = !isHandy || licenses.hasHandheldPro || licenses.hasHandheldOrder;

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Bong Settings)', { fields: data });
    commitToStorage(data);
  }

  /** Only Zettle dock printers support bong prints from the handheld itself */
  const zettleDockTogglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Handy.Settings.UseHandheldPrinter'),
      key: 'useHandheldPrinter',
      storage: storage.useHandheldPrinter,
      hideIn: HIDE_IN.TABLET,
    },
    {
      label: i18n('Handy.Settings.UseHandheldBongPrinter'),
      key: 'useHandheldBongPrinter',
      storage: storage.useHandheldBongPrinter,
      hideIn: HIDE_IN.TABLET,
    },
  ];

  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.PrintKitchenBongSlips'),
      subLabel: i18n('Settings.PrintKitchenBongSlips.Text'),
      key: 'enableBongPrints',
      storage: storage.enableBongPrints,
    },
    /**
     * If bong printing is disabled, hide all the other settings since
     * they make no sense.
     */
    {
      label: i18n('Settings.BongPrintByStation'),
      subLabel: i18n('Settings.BongPrintByStation.Text'),
      key: 'bongPrintByStation',
      storage: storage.bongPrintByStation,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
    {
      label: i18n('Settings.GroupKitchenBongsCategory'),
      subLabel: i18n('Settings.GroupKitchenBongsCategory.Text'),
      key: 'bongGroupByCategory',
      storage: storage.bongGroupByCategory,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
    {
      label: i18n('Settings.Printer.EpsonRetry'),
      subLabel: i18n('Settings.Printer.EpsonRetry.Text'),
      key: 'enableEpsonRetry',
      storage: storage.enableEpsonRetry,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
    {
      label: i18n('Settings.AskToPrintBong'),
      subLabel: i18n('Settings.AskToPrintBong.Desc'),
      key: 'askToPrintBong',
      storage: storage.askToPrintBong,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
    {
      label: i18n('Settings.PrintQrPayLaterBongs'),
      subLabel: i18n('Settings.PrintQrPayLaterBongs.Desc'),
      key: 'printQrPayLaterBongs',
      storage: storage.printQrPayLaterBongs,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
  ];

  const voidTogglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.PrintVoidedItems'),
      subLabel: i18n('Settings.PrintVoidedItems.Desc'),
      key: 'printVoidedItems',
      storage: storage.printVoidedItems,
    },
  ];

  const coursesTogglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.AutoCourseWait'),
      subLabel: i18n('Settings.AutoCourseWait.Desc'),
      key: 'autoAddCourseWait',
      storage: storage.autoAddCourseWait,
    },
    {
      label: i18n('Settings.ExcludeUnmatchedCourseGroups'),
      subLabel: i18n('Settings.ExcludeUnmatchedCourseGroups.Desc'),
      key: 'useNewPrinterFlow',
      storage: storage.useNewPrinterFlow,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
    {
      label: i18n('Settings.GroupKitchenBongsStation'),
      subLabel: i18n('Settings.GroupKitchenBongsStation.Text'),
      key: 'bongGroupByStation',
      storage: storage.bongGroupByStation,
      hideIn: !storage.enableBongPrints ? HIDE_IN.BOTH : undefined,
    },
  ];

  const stylingTogglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.Bong.SortItemsAlphabetically'),
      subLabel: i18n('Settings.Bong.SortItemsAlphabetically.Text'),
      key: 'bongSortItemsAlphabetically',
      storage: storage.bongSortItemsAlphabetically,
    },
    {
      label: i18n('Settings.Bong.DisableCapitalLetters'),
      subLabel: i18n('Settings.Bong.DisableCapitalLetters.Text'),
      key: 'BongDisableCapitalLetters',
      storage: storage.BongDisableCapitalLetters,
      hideIn: hasDefaultTemplate ? HIDE_IN.BOTH : undefined, // If there is a template, the capital letters setting is overridden
    },
    {
      label: i18n('Settings.Bong.HideAllExceptDesiredTime'),
      subLabel: i18n('Settings.Bong.HideAllExceptDesiredTime.Text'),
      key: 'BongHideAllExceptDesiredTime',
      storage: storage.BongHideAllExceptDesiredTime,
      hideIn: hasDefaultTemplate ? HIDE_IN.BOTH : undefined, // If there is a template, the hide all except desired time setting is overridden
    },
    {
      label: i18n('Settings.Bong.ShowFloorplan'),
      subLabel: i18n('Settings.Bong.ShowFloorplan.Text'),
      key: 'bongShowFloorplan',
      storage: storage.bongShowFloorplan,
      hideIn: hasDefaultTemplate ? HIDE_IN.BOTH : undefined, // If there is a template, the show floorplan setting is overridden
    },
    {
      label: i18n('Settings.Bong.ShowPrintId'),
      subLabel: i18n('Settings.Bong.ShowPrintId.Text'),
      key: 'BongShowPrintId',
      storage: storage.BongShowPrintId,
      hideIn: hasDefaultTemplate ? HIDE_IN.BOTH : undefined, // If there is a template, the show print id setting is overridden
    },
    {
      label: i18n('Settings.Bong.ShowPrices'),
      subLabel: i18n('Settings.Bong.ShowPrices.Text'),
      key: 'bongShowPrices',
      storage: storage.bongShowPrices,
      hideIn: hasDefaultTemplate ? HIDE_IN.BOTH : undefined, // If there is a template, the show prices setting is overridden
    },
  ];

  const legacyTogglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.DisableImagePrinting'),
      subLabel: i18n('Settings.DisableImagePrinting.Desc'),
      key: 'disableImagePrinting',
      storage: storage.disableImagePrinting,
    },
  ];

  if (storageLoading) {
    return null;
  }

  // Bail out before any setting is rendered, so a new setting can't slip past the license check
  if (!hasLicense) {
    return <LicenseRequired text={i18n('ClaimLicense.HandheldProOrOrderRequired')} />;
  }

  /** Renders a toggle row from a toggle config entry. */
  const renderToggle = (toggle: TogglesConfig) =>
    !isHiddenOnDevice(toggle.hideIn, isHandy) && (
      <Toggle
        key={toggle.key}
        settingKey={toggle.key}
        label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
        description={toggle.subLabel}
        initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
        onToggle={() => {
          const newValue = !toggle.storage;
          // When enabling Epson retry, automatically enable BongShowPrintId to help identify duplicate prints
          if (toggle.key === 'enableEpsonRetry' && newValue) {
            updateStorage({ [toggle.key]: newValue, BongShowPrintId: true });
          } else {
            updateStorage({ [toggle.key]: newValue });
          }
        }}
        fullWidth
        darkBackground
        lightText
        compact={isHandy}
      />
    );

  /** Skip a whole block when every toggle in it is hidden, so no empty box is rendered */
  const hasVisibleToggles = (toggles: TogglesConfig[]) =>
    toggles.some((toggle) => !isHiddenOnDevice(toggle.hideIn, isHandy));

  const showZettleDock =
    Boolean(deviceInfo?.isZettleTerminal) && hasVisibleToggles(zettleDockTogglesConfig);

  return (
    <Box
      padding={isHandy ? 'none' : 'large'}
      marginTop="small"
      display="flex"
      flexDirection="column"
      gap={isHandy ? 'small' : 'large'}>
      <Box display="flex" flexDirection="column" gap={isHandy ? 'small' : 'large'}>
        {hasVisibleToggles(togglesConfig) && (
          <SettingsBlock compact={isHandy}>{togglesConfig.map(renderToggle)}</SettingsBlock>
        )}

        {showZettleDock && (
          <SettingsBlock compact={isHandy} title={i18n('Settings.ZettleDock')}>
            {zettleDockTogglesConfig.map(renderToggle)}
          </SettingsBlock>
        )}

        <SettingsBlock compact={isHandy} title={i18n('Settings.Bong.VoidTickets')}>
          {voidTogglesConfig.map(renderToggle)}
        </SettingsBlock>

        <SettingsBlock compact={isHandy} title={i18n('Settings.Bong.Courses')}>
          {coursesTogglesConfig.map(renderToggle)}
        </SettingsBlock>

        <SettingsBlock compact={isHandy} title={i18n('Settings.Bong.Styling')}>
          {stylingTogglesConfig.map(renderToggle)}
        </SettingsBlock>
      </Box>

      <SettingsBlock compact={isHandy} title={i18n('Settings.Legacy')}>
        {legacyTogglesConfig.map(renderToggle)}
      </SettingsBlock>
    </Box>
  );
};

export default KitchenTicketsSettings;
